import { useAuthBridgeStore } from "./auth-bridge.store.js";
import type { UserInfo } from "./auth.context.js";

export type AuthHeaders = Record<string, string>;

export interface ApiCallContext {
  headers: AuthHeaders;
  user: UserInfo;
}

/**
 * Overload for type inference from callback function.
 * TypeScript will infer argument types from the callback:
 * `withAuth(async (ctx) => ...)`              - no args
 * `withAuth(async (ctx, id) => ...)`          - single arg
 * `withAuth(async (ctx, id, name) => ...)`    - multiple args
 */
export function withAuth<TArgs extends unknown[], TResult>(
  apiCall: (context: ApiCallContext, ...args: TArgs) => Promise<TResult>
): (...args: TArgs) => Promise<TResult>;

/**
 * Overload for explicit return type only (no arguments).
 * Use this when you want to explicitly type a no-arg function:
 * `withAuth<OfficeResponse>(...)`
 */
export function withAuth<TResult>(
  apiCall: (context: ApiCallContext) => Promise<TResult>
): () => Promise<TResult>;

/**
 * Overload for explicit argument and return types.
 * Use this when you want to provide explicit type arguments:
 * `withAuth<string, GetTeamResponse>(...)`
 *
 * For multi-arg functions with explicit types, use a tuple:
 * `withAuth<[string, number], Result>(...)`
 */
export function withAuth<TArg, TResult>(
  apiCall: (context: ApiCallContext, arg: TArg) => Promise<TResult>
): (arg: TArg) => Promise<TResult>;

export function withAuth<TArgs extends unknown[], TResult>(
  apiCall: (context: ApiCallContext, ...args: TArgs) => Promise<TResult>
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs) => {
    // ? This is a bridge between AuthProvider and normal JS functions. Because the useAuth
    // ? hook cannot be used outside of react components and we need it for API calls, this bridge is
    // ? required.
    const { getAccessToken, user } = useAuthBridgeStore.getState();
    const accessToken = await getAccessToken();

    if (!(accessToken && user)) {
      throw new Error(
        "No access token or user found when calling API endpoint with auth decorator"
      );
    }

    return await apiCall(
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "X-BeZoned-Auth": `Bearer ${accessToken}`,
        },
        user,
      },
      ...args
    );
  };
}
